import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/campaign";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENERIC_MESSAGE =
  "Hi Dr. Crosson's team,\n\nI'd like to talk before booking a visit.\n\nMy questions: ";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ContactFormDialog = ({ open, onOpenChange, prefill, variant }) => {
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const lastOpen = useRef(false);

  useEffect(() => {
    if (open && !lastOpen.current) {
      setForm((f) => ({ ...f, message: prefill || GENERIC_MESSAGE }));
      setStatus("idle");
      setErrors({});
      track(variant, "contact_open");
    }
    lastOpen.current = open;
  }, [open, prefill, variant]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first.trim()) errs.first = "Please enter your first name.";
    if (!form.last.trim()) errs.last = "Please enter your last name.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.message.trim()) errs.message = "Please include a message.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      await axios.post(`${API}/inquiries`, {
        first_name: form.first.trim(),
        last_name: form.last.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message,
        company: e.target.elements.company?.value || null,
      });
      setStatus("success");
      track(variant, "contact_submit");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
        {status === "success" ? (
          <div data-testid="contact-success-message" className="py-8 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-[var(--brand-gold)]" aria-hidden="true" />
            <h2 className="mt-4 font-serif-brand text-2xl font-medium text-[var(--brand-navy)]">
              Thank you.
            </h2>
            <p className="mt-3 text-base leading-relaxed">
              Your message has been sent to the team at All-In Precision Wellness. We&rsquo;ll
              contact you to discuss your questions and next steps.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <DialogTitle className="font-serif-brand text-2xl font-medium text-[var(--brand-navy)]">
                Send a question to the office
              </DialogTitle>
              <DialogDescription className="text-base text-[var(--brand-muted)]">
                Review and edit your message before sending. Share only what you&rsquo;re
                comfortable including.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" noValidate>
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cf-first">First name *</Label>
                  <Input
                    id="cf-first"
                    data-testid="contact-first-name-input"
                    value={form.first}
                    onChange={setField("first")}
                    autoComplete="given-name"
                    className="mt-1.5"
                  />
                  {errors.first && <p className="mt-1 text-sm text-red-700">{errors.first}</p>}
                </div>
                <div>
                  <Label htmlFor="cf-last">Last name *</Label>
                  <Input
                    id="cf-last"
                    data-testid="contact-last-name-input"
                    value={form.last}
                    onChange={setField("last")}
                    autoComplete="family-name"
                    className="mt-1.5"
                  />
                  {errors.last && <p className="mt-1 text-sm text-red-700">{errors.last}</p>}
                </div>
              </div>
              <div>
                <Label htmlFor="cf-email">Email *</Label>
                <Input
                  id="cf-email"
                  data-testid="contact-email-input"
                  type="email"
                  inputMode="email"
                  value={form.email}
                  onChange={setField("email")}
                  autoComplete="email"
                  className="mt-1.5"
                />
                {errors.email && <p className="mt-1 text-sm text-red-700">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="cf-phone">Phone (optional)</Label>
                <Input
                  id="cf-phone"
                  data-testid="contact-phone-input"
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={setField("phone")}
                  autoComplete="tel"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="cf-message">Your message *</Label>
                <Textarea
                  id="cf-message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={setField("message")}
                  rows={9}
                  className="mt-1.5 text-base"
                />
                {errors.message && <p className="mt-1 text-sm text-red-700">{errors.message}</p>}
              </div>
              {status === "error" && (
                <p data-testid="contact-error-message" className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">
                  Your message could not be sent. Nothing was lost. Please try again, or call{" "}
                  <a href={PHONE_TEL} data-call-location="contact_dialog" className="font-semibold underline">
                    {PHONE_DISPLAY}
                  </a>
                  .
                </p>
              )}
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors disabled:opacity-60"
              >
                {status === "submitting" && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
                Send My Question
              </button>
              <p className="text-xs leading-relaxed text-[var(--brand-muted)]">
                Your message is delivered by email to the team at All-In Precision Wellness and to
                the practice&rsquo;s website agency, Bliss Branding, which helps manage these
                inquiries. It is not shared with advertising platforms. Please don&rsquo;t include
                detailed medical history here; your visit is the right place for that conversation.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
